/**
 * LocalStorage persistence for the Apply For Me upsell promo shown on the
 * auto-apply success page.
 *
 * This module is intentionally **I/O only**: it reads and writes the raw promo
 * state object and validates its shape. It knows nothing about the eligibility
 * rules (submission threshold, suppression window, draft dedupe) — those live
 * with the React adapter in `useApplyForMePromo`, which is the only caller of
 * {@link getAfmPromoState} / {@link saveAfmPromoState}.
 *
 * SSR-safe: every accessor no-ops when `window` is unavailable.
 */

export interface AfmPromoState {
    /** Draft IDs already counted toward `submittedCount` (dedupes page refreshes). */
    countedDraftIds: string[];
    /** Cumulative number of drafts the user has submitted. */
    submittedCount: number;
    /** Epoch ms until which the promo stays suppressed (0 = not dismissed). */
    dismissedUntil: number;
    /** Permanently suppress the promo once the user has entered Apply For Me setup. */
    setUp: boolean;
}

const STORAGE_KEY = 'afm_promo';

const DEFAULT_STATE: AfmPromoState = {
    countedDraftIds: [],
    submittedCount: 0,
    dismissedUntil: 0,
    setUp: false,
};

function isValidState(value: unknown): value is AfmPromoState {
    return (
        value !== null &&
        typeof value === 'object' &&
        Array.isArray((value as AfmPromoState).countedDraftIds) &&
        typeof (value as AfmPromoState).submittedCount === 'number' &&
        typeof (value as AfmPromoState).dismissedUntil === 'number' &&
        typeof (value as AfmPromoState).setUp === 'boolean'
    );
}

export function getAfmPromoState(): AfmPromoState {
    if (typeof window === 'undefined') {
        return { ...DEFAULT_STATE };
    }

    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...DEFAULT_STATE };
        const parsed = JSON.parse(raw) as unknown;
        if (!isValidState(parsed)) {
            localStorage.removeItem(STORAGE_KEY);
            return { ...DEFAULT_STATE };
        }
        return parsed;
    } catch {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            // ignore
        }
        return { ...DEFAULT_STATE };
    }
}

export function saveAfmPromoState(state: AfmPromoState): void {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
        console.warn('afm_promo localStorage error', error);
    }
}
